import React, { ComponentProps } from "react";
import { useCheckboxGroupState } from "react-stately";
import { AriaCheckboxGroupProps, useCheckboxGroup } from "react-aria";
import { CheckboxFieldError } from "atom/checkbox/checkboxComponents";
import { ErrorIcon } from "constants/Icon";
import { CheckboxGroupState } from "atom/autoform/AutoFormBloc";
import z from "zod";
import {
  CheckboxGroup as Acb,
  FieldError,
  Label,
  Text
} from "react-aria-components";
import styled from "@emotion/styled";

const CheckboxGroupContainer = styled(Acb)`
  display: flex;
  flex-direction: column;
  gap: var(--group-item-gap, 1rem);
`;

export type CheckboxGroupProps = AriaCheckboxGroupProps & {
  children: React.ReactNode;
  errorParser?: (props: CheckboxGroupProps) => string;
  error?: string | z.ZodIssue;
  ref?: React.Ref<CheckboxGroupState>;
};

export default function CheckboxGroup(props: CheckboxGroupProps) {
  const { children, label, description } = props;
  const extendedProps: CheckboxGroupProps = {
    ...props,
    validationBehavior: "aria"
  };

  const state = useCheckboxGroupState(extendedProps);
  const checkboxGroup = useCheckboxGroup(extendedProps, state);
  React.useImperativeHandle(props.ref, () => state, [state]);

  const error = <FieldError>{props.errorMessage}</FieldError>;

  return (
    <CheckboxGroupContainer
      validationBehavior="aria"
      name={props.name}
      value={props.value}
      {...(checkboxGroup.groupProps as ComponentProps<typeof Acb>)}
      onChange={props.onChange}
    >
      {label && <Label>{label}</Label>}
      {children}
      {description && <Text slot="description">{description}</Text>}
      <CheckboxFieldError>
        <div className="little1">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5em"
            }}
          >
            <div aria-hidden="true">
              <ErrorIcon />
            </div>
            <span className="field-error">{error}</span>
          </div>
        </div>
      </CheckboxFieldError>
    </CheckboxGroupContainer>
  );
}
